import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api.js";
import { useSendMagicLink } from "../hooks/useApiQueries.js";

type VerifyOk = { ok: true; redirectTo: string; email: string };
type VerifyErr = { ok: false; error: "invalid" | "expired" | "already-used" | "not-allowed"; email?: string };

const ERROR_MESSAGES: Record<VerifyErr["error"], string> = {
  invalid: "That sign-in link is invalid. Request a new one below.",
  expired: "That sign-in link has expired. They're good for 30 minutes — request a new one below.",
  "already-used": "That sign-in link has already been used. Request a new one below.",
  "not-allowed": "That account isn't authorised to sign in. Ask the Corian Park team to add it.",
};

export function Verify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errorState, setErrorState] = useState<{ message: string; email?: string } | null>(null);
  const resendMutation = useSendMagicLink();
  const [resendEmail, setResendEmail] = useState("");

  const verify = useMutation({
    mutationFn: async (t: string) =>
      api<VerifyOk>("/v1/auth/verify", { method: "POST", body: JSON.stringify({ token: t }) }),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["auth"] });
      navigate(res.redirectTo, { replace: true });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 400) {
        const payload = err.payload as VerifyErr | undefined;
        const key = payload?.error ?? "invalid";
        setErrorState({ message: ERROR_MESSAGES[key], email: payload?.email });
        if (payload?.email) setResendEmail(payload.email);
      } else {
        setErrorState({ message: "Couldn't reach the server. Check your connection and try again." });
      }
    },
  });

  useEffect(() => {
    if (!token) {
      setErrorState({ message: "No sign-in token in the URL." });
      return;
    }
    verify.mutate(token);
    // Intentionally only depend on token — useMutation is stable, and we
    // want exactly one verify call per token render.
  }, [token]);

  if (errorState) {
    return (
      <Box sx={{ maxWidth: 480, mx: "auto", py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorState.message}
        </Alert>
        {errorState.email && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Send a new link to <strong>{errorState.email}</strong>?
            </Typography>
            {resendMutation.isSuccess ? (
              <Alert severity="success">Check your inbox — new link sent.</Alert>
            ) : (
              <Button
                variant="contained"
                color="primary"
                disabled={resendMutation.isPending || !resendEmail}
                onClick={() => resendMutation.mutate(resendEmail)}
              >
                {resendMutation.isPending ? "Sending…" : "Email me a new link"}
              </Button>
            )}
            {resendMutation.error && (
              <Alert severity="error">Couldn't send a new link. Try again in a minute.</Alert>
            )}
          </Stack>
        )}
        {!errorState.email && (
          <Button variant="outlined" onClick={() => navigate("/auth/sign-in", { replace: true })}>
            Back to sign in
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: "center", py: 8 }}>
      <CircularProgress sx={{ mb: 2 }} />
      <Typography variant="body1">Signing you in…</Typography>
    </Box>
  );
}
